
import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import FAQSchema from '../../../components/feature/FAQSchema';
import InternalLinks from '../../../components/feature/InternalLinks';

const HirsizAlarmSistemiCalismaMantigi = () => {
  useEffect(() => {
    document.title = 'Hırsız Alarm Sistemi Çalışma Mantığı | Güvenlik Rehberi | FiberNet Teknoloji';

    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMetaTag('description', 'Hırsız alarm sistemlerinin çalışma mantığı, sensör türleri ve fabrika uygulamaları hakkında detaylı rehber. PIR sensörler, manyetik kontaklar ve modern güvenlik teknolojileri.');
    updateMetaTag('keywords', 'hırsız alarm sistemi, güvenlik sensörleri, PIR sensör, manyetik kontak, fabrika güvenlik, sanayi alarmı');
    updateMetaTag('author', 'FiberNet Teknoloji');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('og:title', 'Hırsız Alarm Sistemi Çalışma Mantığı | FiberNet Teknoloji', true);
    updateMetaTag('og:description', 'Hırsız alarm sistemlerinin çalışma mantığı, sensör türleri ve fabrika uygulamaları hakkında detaylı rehber.', true);
    updateMetaTag('og:image', 'https://public.readdy.ai/ai/img_res/0231594dc34b3ca59d5ec5d9eb504567.jpg', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://fibernetteknoloji.com/blog/hirsiz-alarm-sistemi-calisma-mantigi';
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: 'Hırsız Alarm Sistemi Çalışma Mantığı' }
  ];

  const faqData = [
    {
      question: "Hırsız alarm sistemleri nasıl çalışır?",
      answer: "Hırsız alarm sistemleri hareket sensörleri, manyetik kontak sensörleri ve cam kırılma sensörleri gibi çeşitli algılayıcılarla çalışır. Bu sensörler anormal durumları tespit ettiğinde alarm paneline sinyal gönderir ve sistem devreye girer."
    },
    {
      question: "Fabrika ve sanayi tesislerinde hangi sensör türleri kullanılır?",
      answer: "Fabrika alanları için PIR hareket sensörleri, çit alarmları, beam break sensörler, titreşim algılayıcıları ve kamera entegreli motion detektörler kullanılır. Açık alan koruma için özel dış mekan sensörleri tercih edilir."
    },
    {
      question: "Alarm sistemi yanlış alarm vermesini nasıl önleriz?",
      answer: "Çifte doğrulama sistemi, pet immunity özellikli sensörler, uygun montaj yüksekliği ve kaliteli ekipman kullanımı ile yanlış alarmlar minimum seviyeye indirilir. Düzenli bakım da önemlidir."
    },
    {
      question: "Kablosuz alarm sistemleri güvenilir midir?",
      answer: "Modern kablosuz alarm sistemleri şifreli haberleşme ve anti-jamming teknolojileri ile oldukça güvenlidir. Pil durumu izleme ve sinyal gücü kontrolü ile güvenilirlik artırılır."
    }
  ];

  return (
    <>
      <Navbar />
      
      <main className="min-h-screen bg-white">
        <section className="bg-gray-50 py-4">
          <div className="container mx-auto px-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        <section className="py-12 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
                Güvenlik Sistemleri
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Hırsız Alarm Sistemi Çalışma Mantığı
              </h1>
              <p className="text-xl text-teal-100 mb-8">
                Modern hırsız alarm sistemlerinin çalışma prensibi, sensör türleri ve fabrika uygulamaları hakkında kapsamlı rehber
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-teal-100">
                <span>📅 15 Ocak 2025</span>
                <span>⏱️ 8 dakika okuma</span>
                <span>🔒 Güvenlik Sistemleri</span>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              <div className="mb-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20security%20alarm%20control%20panel%20interface%20with%20multiple%20zones%20display%2C%20motion%20sensors%20PIR%20detectors%2C%20magnetic%20door%20contacts%2C%20modern%20security%20technology%20equipment%20in%20clean%20industrial%20environment&width=800&height=450&seq=alarm-control-panel&orientation=landscape"
                  alt="Hırsız Alarm Sistemi Kontrol Paneli"
                  className="w-full h-96 object-cover rounded-lg shadow-lg"
                />
              </div>

              <div className="prose max-w-none">
                
                <div className="mb-12">
                  <p className="text-lg text-gray-700 leading-8 mb-6">
                    Modern hırsız alarm sistemleri, güvenlik teknolojisinin en önemli bileşenlerinden biridir. Bu sistemlerin doğru çalışması için temel çalışma mantığını ve bileşenlerini anlamak kritik öneme sahiptir. Özellikle fabrika ve sanayi tesislerinde kullanılan alarm sistemleri, geniş alanları koruma konusunda özel tasarım gerektirir.
                  </p>
                </div>

                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Hırsız Alarm Sisteminin Temel Çalışma Mantığı</h2>
                  <div className="bg-teal-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold text-teal-800 mb-4">Sistem Bileşenleri</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li><strong>Kontrol Paneli:</strong> Tüm sensörlerden gelen sinyalleri işler ve alarm durumunu yönetir</li>
                      <li><strong>Sensörler:</strong> Hareket, kapı/pencere açılması, cam kırılması gibi olayları algılar</li>
                      <li><strong>Siren/Uyarı Sistemleri:</strong> Alarm durumunda sesli/ışıklı uyarı verir</li>
                      <li><strong>İletişim Modülleri:</strong> GSM/ethernet üzerinden merkeze bildirim yapar</li>
                    </ul>
                  </div>
                  <p className="text-gray-700 leading-8">
                    Sistem normalde "hazır" konumda bekler. Herhangi bir sensör tetiklendiğinde, kontrol paneli bu sinyali değerlendirir ve önceden programlanmış tepkileri devreye sokar. Bu süreç genellikle milisaniyeler içinde gerçekleşir.
                  </p>
                </div>

                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Alarm Sistemlerinde Kullanılan Sensör Türleri</h2>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">🚶 PIR Hareket Sensörleri</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Kızılötesi ısı değişimini algılar</li>
                        <li>• 10-15 metre algılama mesafesi</li>
                        <li>• Pet immunity özelliği</li>
                        <li>• Açı ayarı yapılabilir</li>
                      </ul>
                    </div>
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">🚪 Manyetik Kontak Sensörleri</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Kapı/pencere açılmasını algılar</li>
                        <li>• Manyetik alan değişimi prensibi</li>
                        <li>• Gizli veya görünür montaj</li>
                        <li>• Düşük güç tüketimi</li>
                      </ul>
                    </div>
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">🔊 Ses/Titreşim Sensörleri</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Cam kırılması sesini algılar</li>
                        <li>• Duvarda delme/kırma tespiti</li>
                        <li>• Frekans filtreleme özelliği</li>
                        <li>• Ayarlanabilir hassasiyet</li>
                      </ul>
                    </div>
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">📡 Beam Break Sensörler</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Görünmez ışık bariyeri</li>
                        <li>• Açık alan koruma</li>
                        <li>• 100+ metre menzil</li>
                        <li>• Çoklu beam teknolojisi</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Fabrika ve Sanayi Tesislerinde Özel Uygulamalar</h2>
                  
                  <div className="mb-8">
                    <img 
                      src="https://readdy.ai/api/search-image?query=industrial%20factory%20security%20system%20installation%20with%20outdoor%20motion%20sensors%2C%20perimeter%20protection%2C%20beam%20break%20detectors%20on%20fence%20line%2C%20professional%20security%20equipment%20in%20manufacturing%20facility&width=800&height=400&seq=factory-security&orientation=landscape"
                      alt="Fabrika Güvenlik Sistemi"
                      className="w-full h-64 object-cover rounded-lg shadow-lg mb-6"
                    />
                  </div>

                  <div className="bg-orange-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold text-orange-800 mb-4">Sanayi Tesisleri İçin Özel Gereksinimler</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-orange-700 mb-2">Çevre Koruma</h4>
                        <ul className="space-y-1 text-gray-700 text-sm">
                          <li>• Çit üzeri sensörler</li>
                          <li>• Perimeter beam sistemleri</li>
                          <li>• Termal kameralar</li>
                          <li>• Radar teknolojisi</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-orange-700 mb-2">İç Alan Koruma</h4>
                        <ul className="space-y-1 text-gray-700 text-sm">
                          <li>• Yüksek tavanlı PIR'lar</li>
                          <li>• Çok zonlu algılayıcılar</li>
                          <li>• Titreşim sensörleri</li>
                          <li>• Kamera entegrasyonu</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 leading-8 mb-6">
                    Fabrika ortamlarında alarm sistemleri, normal konut uygulamalarından farklı zorluklar yaşar. Büyük açık alanlar, yüksek tavanlı binalar, makine titreşimleri ve çalışan personelin varlığı özel sensör konfigürasyonları gerektirir.
                  </p>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Fabrika Alarm Sistemi Tasarım Prensipleri</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>✓ <strong>Zonlama:</strong> Fabrikanın farklı bölümlerini ayrı zonlara ayırma</li>
                      <li>✓ <strong>Zaman Programları:</strong> Vardiya saatlerine göre otomatik devreye alma/çıkarma</li>
                      <li>✓ <strong>Çifte Doğrulama:</strong> Yanlış alarm riskini minimize etme</li>
                      <li>✓ <strong>Merkezi İzleme:</strong> 7/24 profesyonel güvenlik merkezi bağlantısı</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Modern Alarm Teknolojilerindeki Gelişmeler</h2>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="text-center p-6 bg-teal-50 rounded-lg">
                      <div className="w-16 h-16 mx-auto mb-4 bg-teal-100 rounded-full flex items-center justify-center">
                        <i className="ri-smartphone-line text-2xl text-teal-600"></i>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">Akıllı Entegrasyon</h3>
                      <p className="text-sm text-gray-600">Mobil uygulama kontrolü ve uzaktan yönetim</p>
                    </div>
                    <div className="text-center p-6 bg-orange-50 rounded-lg">
                      <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
                        <i className="ri-ai-generate text-2xl text-orange-600"></i>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">AI Destekli Analiz</h3>
                      <p className="text-sm text-gray-600">Yapay zeka ile yanlış alarm önleme</p>
                    </div>
                    <div className="text-center p-6 bg-blue-50 rounded-lg">
                      <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-cloud-line text-2xl text-blue-600"></i>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">Bulut Tabanlı</h3>
                      <p className="text-sm text-gray-600">Merkezi yönetim ve raporlama sistemleri</p>
                    </div>
                  </div>

                  <p className="text-gray-700 leading-8">
                    Günümüzde hırsız alarm sistemleri sadece basit sensörlerden ibaret değil. IoT teknolojisi, yapay zeka algoritmaları ve bulut tabanlı yönetim sistemleri ile entegre edilen modern çözümler, hem güvenilirliği artırıyor hem de yönetimi kolaylaştırıyor.
                  </p>
                </div>

                <div className="mb-12">
                  <div className="bg-gradient-to-r from-teal-50 to-orange-50 p-8 rounded-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">İlgili Hizmetlerimiz</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <a 
                        href="/security-systems/burglar-alarm-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-alarm-warning-line text-xl text-teal-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600">Hırsız Alarm Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Fabrika ve işyerleri için profesyonel hırsız alarm sistemi kurulumu ve bakım hizmetleri.</p>
                      </a>
                      
                      <a 
                        href="/security-systems/access-control-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-key-2-line text-xl text-orange-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-orange-600">Geçiş Kontrol Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Kartlı geçiş, parmak izi okuyucu ve yüz tanıma teknolojileri ile güvenli erişim kontrolü.</p>
                      </a>

                      <a 
                        href="/security-systems/ip-camera-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-camera-line text-xl text-blue-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">IP Kamera Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Yüksek çözünürlüklü IP kamera sistemleri ile 7/24 güvenlik izleme çözümleri.</p>
                      </a>

                      <a 
                        href="/security-systems/fire-alarm-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-fire-line text-xl text-red-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-red-600">Yangın Alarm Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">TSE standartlarına uygun yangın algılama ve alarm sistemleri kurulumu.</p>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Sıkça Sorulan Sorular</h2>
                  <FAQSchema faqs={faqData} />
                </div>

                <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8 rounded-lg text-center">
                  <h2 className="text-2xl font-bold mb-4">Profesyonel Hırsız Alarm Sistemi Kurulumu</h2>
                  <p className="text-teal-100 mb-6">
                    Fabrika ve işyeriniz için en uygun alarm sistemi çözümlerini keşfedin. 
                    Uzman ekibimizden ücretsiz keşif ve teklif alın.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href="tel:+905537165561" 
                      className="bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-all duration-300 whitespace-nowrap"
                    >
                      📞 Hemen Ara: +90 553 716 55 61
                    </a>
                    <a 
                      href="/contact" 
                      className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-all duration-300 whitespace-nowrap"
                    >
                      💬 Online Teklif Al
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        <InternalLinks />
      </main>

      <Footer />
      <FloatingButtons />
    </>
  );
};

export default HirsizAlarmSistemiCalismaMantigi;
